import { NetworkIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { useExportChecklistSignature } from '@/api/checklists/export-checklist-signature'
import { Icon } from '@/components/ui/icon'
import { TableColumn } from '@/components/ui/table'
import { EMPTY_VALUE } from '@/constants'
import { toastFactory } from '@/factories'
import i18n from '@/i18n'
import { downloadHref, getFilenameFromUrl } from '@/utils'

import { ChecklistTaskAnswer } from './checklist-task-answer/checklist-task-answer'
import * as SS from './checklist-task-answer/checklist-task-answer.styled'
import * as S from './checklists-task.page.styled'
import { ChecklistTaskRow } from './checklists-task.page.types'
import {
  formatChecklistInputType,
  isChecklistAttachmentRow,
  isChecklistInputTypeForm,
  isChecklistInstructionsRow,
  isChecklistSignaturesRow,
} from './checklists-task.page.utils'

const handleDownload = async (url: string, fileName: string) => {
  try {
    await downloadHref(url, fileName)
  } catch {
    toastFactory.error(i18n.t('checklists:task.downloadError'))
  }
}

const ChecklistSignatureDownloadButton: FC<{ checklistId: number }> = ({ checklistId }) => {
  const { t } = useTranslation(['checklists'])
  const { isPending, mutate: exportChecklistSignature } = useExportChecklistSignature()

  const downloadSignature = () => {
    exportChecklistSignature({ checklistId, fileName: `signature-${checklistId}.png` })
  }

  return (
    <SS.FileDownloadButton disabled={isPending} type="button" onClick={downloadSignature}>
      {t('checklists:task.signatureFileName')}
    </SS.FileDownloadButton>
  )
}

// Note: Columns are described with % to match the grid layout for collapsable row.
// Check ./checklist-input-form-row for more details.
export const getChecklistTaskColumns = (
  t: TFunction<['checklists']>,
): TableColumn<ChecklistTaskRow>[] => [
  {
    id: 'type',
    render: (input) => {
      if (isChecklistAttachmentRow(input)) {
        return t('checklists:task.rowTypes.attachment')
      }

      if (isChecklistInstructionsRow(input)) {
        return t('checklists:task.rowTypes.instructions')
      }

      if (isChecklistSignaturesRow(input)) {
        return t('checklists:task.rowTypes.signatures')
      }

      if (isChecklistInputTypeForm(input)) {
        return (
          <S.FormLabel>
            {formatChecklistInputType(input.inputType, t)}
            <S.FormLabelIconContainer>
              <Icon icon={NetworkIcon} size="2rem" color="blue500" />
            </S.FormLabelIconContainer>
          </S.FormLabel>
        )
      }

      return formatChecklistInputType(input.inputType, t)
    },
    title: t('checklists:task.columns.type'),
    width: '25%',
  },
  {
    id: 'label',
    render: (input) => {
      if (isChecklistAttachmentRow(input)) {
        return ''
      }

      if (isChecklistInstructionsRow(input)) {
        return ''
      }

      if (isChecklistSignaturesRow(input)) {
        return ''
      }

      return input.taskLabel
    },
    title: t('checklists:task.columns.label'),
    width: '25%',
  },
  {
    id: 'answer',
    render: (input) => {
      if (isChecklistAttachmentRow(input)) {
        if (!input.url) {
          return EMPTY_VALUE
        }

        const fileName = getFilenameFromUrl(input.url)

        return (
          <SS.FileDownloadButton type="button" onClick={() => handleDownload(input.url, fileName)}>
            {fileName}
          </SS.FileDownloadButton>
        )
      }

      if (isChecklistInstructionsRow(input)) {
        return <SS.InnerHtmlContainer dangerouslySetInnerHTML={{ __html: input.html }} />
      }

      if (isChecklistSignaturesRow(input)) {
        return <ChecklistSignatureDownloadButton checklistId={input.checklistId} />
      }

      return <ChecklistTaskAnswer input={input} />
    },
    title: t('checklists:task.columns.answer'),
    width: '50%',
  },
]
